/**
 * Assets API hooks — TanStack Query wrappers for the /api/assets endpoints.
 *
 * Same scoping model as events:
 *   scope=account   → all assets the user can see (read-only)
 *   scope=workspace → assets in a given workspace  (read-only)
 *   scope=film      → assets in a given film        (full CRUD)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../config/api';

/* ── Key factories ────────────────────────────────────────────── */
export const assetKeys = {
  all: ['assets'],
  account: (filters) => ['assets', 'account', filters],
  workspace: (wsId, filters) => ['assets', 'workspace', wsId, filters],
  film: (filmId, filters) => ['assets', 'film', filmId, filters],
  detail: (id) => ['assets', 'detail', id],
};

/* ── Fetch helpers ────────────────────────────────────────────── */
async function fetchAssets({ scope, workspace_id, film_id, ownership, category }) {
  const params = { scope };
  if (workspace_id) params.workspace_id = workspace_id;
  if (film_id) params.film_id = film_id;
  if (ownership) params.ownership = ownership;
  if (category) params.category = category;
  const { data } = await apiClient.get('/assets', { params });
  return data;
}

async function fetchAsset(id) {
  const { data } = await apiClient.get(`/assets/${id}`);
  return data;
}

async function createAsset(body) {
  const { data } = await apiClient.post('/assets', body);
  return data;
}

async function updateAsset({ id, ...body }) {
  const { data } = await apiClient.patch(`/assets/${id}`, body);
  return data;
}

async function deleteAsset(id) {
  await apiClient.delete(`/assets/${id}`);
}

/* ── Query hooks ──────────────────────────────────────────────── */

export function useAssets({ scope = 'account', workspace_id, film_id, ownership, category } = {}) {
  const filters = { ownership, category };
  const key =
    scope === 'film'
      ? assetKeys.film(film_id, filters)
      : scope === 'workspace'
        ? assetKeys.workspace(workspace_id, filters)
        : assetKeys.account(filters);

  return useQuery({
    queryKey: key,
    queryFn: () => fetchAssets({ scope, workspace_id, film_id, ownership, category }),
    enabled:
      scope === 'account' ||
      (scope === 'workspace' && !!workspace_id) ||
      (scope === 'film' && !!film_id),
  });
}

export function useAsset(id) {
  return useQuery({
    queryKey: assetKeys.detail(id),
    queryFn: () => fetchAsset(id),
    enabled: !!id,
  });
}

/* ── Mutation hooks ───────────────────────────────────────────── */

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createAsset,
    onSuccess: () => qc.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateAsset,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: assetKeys.all });
      qc.setQueryData(assetKeys.detail(data.id), data);
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteAsset,
    onSuccess: () => qc.invalidateQueries({ queryKey: assetKeys.all }),
  });
}

/* ── Overlap check ──────────────────────────────────────────────── */
async function fetchOverlaps(assetId, start, end) {
  const params = {};
  if (start) params.start = start;
  if (end) params.end = end;
  const { data } = await apiClient.get(`/assets/${assetId}/overlaps`, { params });
  return data;
}

export function useAssetOverlaps(assetId, start, end) {
  return useQuery({
    queryKey: ['assets', 'overlaps', assetId, start, end],
    queryFn: () => fetchOverlaps(assetId, start, end),
    enabled: !!assetId && (!!start || !!end),
  });
}
